import type { FlowCopy, WallCopy } from "./content.js";
import { PHOTOS, photoById, type WallPhoto } from "./photos.js";
import { CameraScreen, InviteMock, Phone, ScanScreen, SentScreen, SHOT_SRC } from "./screens.js";
import { MenuIcon } from "./icons.js";
import { pad, useScrollSteps } from "./scroll-steps.js";
import { ScrollCue, SnapStops, StepRail, stepOf } from "./step-rail.js";
import { Wall, type WallFeature } from "./wall.js";

const HOST = "Leo & Day";
export const BEFORE = photoById("c14");
export const EARLIER: readonly WallPhoto[] = PHOTOS.filter((photo) => photo.id !== BEFORE.id)
  .slice(-6)
  .reverse();

export interface WallState {
  readonly featured: WallFeature;
  readonly recent: readonly WallPhoto[];
  readonly count: number;
}

export function wallForStep(step: number, guest: string): WallState {
  if (step >= 3) {
    return {
      featured: { key: "guest", src: SHOT_SRC, author: guest },
      recent: [BEFORE, ...EARLIER],
      count: 18,
    };
  }
  return {
    featured: { key: BEFORE.id, src: BEFORE.large, author: BEFORE.author },
    recent: EARLIER,
    count: 17,
  };
}

export type FlowVariant = "a" | "b" | "c" | "d" | "snap";

export interface FlowProps {
  readonly copy: FlowCopy;
  readonly wall: WallCopy;
  readonly variant?: FlowVariant;
}

export { stepOf };

export function Flow({ copy, wall, variant }: FlowProps) {
  const total = copy.steps.length;
  const { track, live, step, idle, go, next } = useScrollSteps(total);

  const guest = `${copy.guest[0]} ${copy.guest[1]}`;
  const screen = wallForStep(step, guest);

  return (
    <section
      class={`flow${live ? " is-live" : ""}${variant ? ` flow-v-${variant} is-snap` : ""}`}
      id="como"
      style={{ "--steps": total }}
      data-step={step}
    >
      <div class="flow-track" ref={track}>
        {variant ? <SnapStops total={total} /> : null}
        <div class="flow-pin">
          <div class="flow-panel" data-theme="dark">
            <div class="flow-copy">
              <p class="micro">{copy.label}</p>
              <h2 class="flow-title serif">{copy.title}</h2>
              <ol class="flow-steps">
                {copy.steps.map((item, index) => (
                  <li
                    key={item.tag}
                    class={index === step ? "flow-step is-on" : "flow-step"}
                    aria-current={index === step ? "step" : undefined}
                  >
                    <p class="flow-tag">
                      {pad(index + 1)} / {item.tag}
                    </p>
                    <h3 class="flow-step-title">{item.title}</h3>
                    <p class="flow-step-body">{item.body}</p>
                  </li>
                ))}
              </ol>
              <div class="flow-ticks">
                {copy.steps.map((item, index) => (
                  <button
                    key={item.tag}
                    type="button"
                    class={index === step ? "flow-tick is-on" : "flow-tick"}
                    aria-label={`${pad(index + 1)} ${item.tag}`}
                    aria-pressed={index === step}
                    onClick={() => go(index)}
                  >
                    <span class="flow-tick-bar" />
                    {pad(index + 1)}
                  </button>
                ))}
              </div>
              <p class="flow-hint">{copy.hint}</p>
              {variant === "c" ? (
                <div class="flow-tabs" data-testid="flow-tabs">
                  {copy.steps.map((item, index) => (
                    <button
                      key={item.tag}
                      type="button"
                      class={index === step ? "flow-tab is-on" : "flow-tab"}
                      aria-pressed={index === step}
                      onClick={() => go(index)}
                    >
                      <b>{index + 1}</b>
                      <span class="flow-tab-tag">{item.tag}</span>
                    </button>
                  ))}
                  {step < total - 1 ? (
                    <button type="button" class="flow-next" onClick={next}>
                      {copy.next}: {copy.steps[step + 1]?.tag}
                      <MenuIcon name="arrow" size={16} />
                    </button>
                  ) : (
                    <p class="flow-next is-last">{copy.nudgeLast}</p>
                  )}
                </div>
              ) : null}
            </div>
            <div class="scene" onClick={next}>
              <div class="scene-tv">
                <Wall
                  copy={wall}
                  host={copy.scan}
                  count={screen.count}
                  featured={screen.featured}
                  recent={screen.recent}
                />
              </div>
              <div class="scene-phone">
                <Phone>
                  <div class={step === 0 ? "phone-page is-on" : "phone-page"}>
                    <ScanScreen copy={copy} />
                  </div>
                  <div class={step === 1 ? "phone-page is-on" : "phone-page"}>
                    <InviteMock
                      copy={copy}
                      host={HOST}
                      accent="var(--action-bg)"
                      line="var(--on-photo-2)"
                      typing={live && step === 1}
                    />
                  </div>
                  <div class={step === 2 ? "phone-page is-on" : "phone-page"}>
                    <CameraScreen copy={copy} flash={live && step === 2} />
                  </div>
                  <div class={step === 3 ? "phone-page is-on" : "phone-page"}>
                    <SentScreen copy={copy} />
                  </div>
                </Phone>
              </div>
            </div>
            {variant === "a" ? (
              <>
                <StepRail labels={copy.steps.map((item) => item.tag)} step={step} go={go} />
                <ScrollCue copy={copy} step={step} total={total} shown={idle} next={next} />
              </>
            ) : null}
            {variant === "b" ? (
              <>
                <div class="flow-progress" data-testid="flow-progress">
                  {copy.steps.map((item, index) => (
                    <button
                      key={item.tag}
                      type="button"
                      class={index <= step ? "is-done" : undefined}
                      aria-label={`${pad(index + 1)} ${item.tag}`}
                      aria-pressed={index === step}
                      onClick={() => go(index)}
                    />
                  ))}
                </div>
                <ScrollCue copy={copy} step={step} total={total} shown next={next} />
              </>
            ) : null}
            {variant === "d" ? (
              <>
                <button
                  type="button"
                  class="flow-count serif"
                  onClick={next}
                  aria-label={stepOf(copy.stepOf, step, total)}
                  data-testid="flow-count"
                >
                  {pad(step + 1)}
                  <small>/{pad(total)}</small>
                </button>
                <button type="button" class="flow-scroll" onClick={next}>
                  {step < total - 1 ? copy.scrollCue : copy.nudgeLast}
                  <span class="flow-chevrons" aria-hidden="true">
                    <MenuIcon name="chevron" size={18} />
                    <MenuIcon name="chevron" size={18} />
                  </span>
                </button>
              </>
            ) : null}
            <p
              class={idle ? "flow-nudge is-on" : "flow-nudge"}
              aria-hidden="true"
              data-testid="flow-nudge"
            >
              <span class="flow-nudge-arrow">
                <MenuIcon name="chevron" size={16} />
              </span>
              {step < total - 1 ? copy.nudge : copy.nudgeLast}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
